import { useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Tooltip, IconButton } from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import EmergencyIcon from "@mui/icons-material/EmergencyOutlined";
import NotificationsIcon from "@mui/icons-material/NotificationsNoneOutlined";
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";

import { generateColumnsDefaultConfiguration } from "../../../components/dataGrid/columnHelpers";
import { PopoverModal } from "../../../components/popoverModal/PopoverModal";
import SimplePopoverOption from "../../../components/popoverModal/SimplePopoverOption";
import DetalleRowTareas from "../components/DetalleRowTareas";
import DetalleRowPacientes from "../components/DetalleRowPacientes";

/**
 * Hook personalizado para generar la configuración de columnas del DataGrid
 * de visitas médicas.
 * 
 * Este hook gestiona las columnas dinámicas según el tipo de visita,
 * incluyendo funcionalidad de expansión de filas para mostrar detalles adicionales.
 * 
 * @param {Object} filters - Filtros aplicados, incluye tipoVisita (1=Tareas, 2=Pacientes)
 * @param {Set} expandedId - Set de IDs de evaluaciones expandidas
 * @param {Function} setExpandedId - Función para actualizar los IDs expandidos
 * @param {Array} rows - Array de filas de datos
 * @returns {Array} Configuración de columnas procesada para el DataGrid
 */

// ============================================================================
// CONSTANTES
// ============================================================================

/** Número de columnas visibles en la vista de Tareas */
const VISIBLE_COL_COUNT_TAREAS = 5;

/** Número de columnas visibles en la vista de Pacientes */
const VISIBLE_COL_COUNT_PACIENTES = 4;

/** Rutas de navegación */
const ROUTES = {
  VISITAS_CONTRATO: "/misVisitas/visitasContrato",
  NOTIFICACIONES: "/misVisitas/notificaciones",
};

/** Tipos de visita */
const TIPO_VISITA = {
  TAREAS: 1,
  PACIENTES: 2,
};

// ============================================================================
// FUNCIONES AUXILIARES
// ============================================================================

/**
 * Determina si una fila es una fila de detalle expandida
 * @param {Object} row - Fila a evaluar
 * @returns {boolean} True si es una fila de detalle
 */
const isDetailRow = (row) => row?.__isDetail === true;

// ============================================================================
// HOOK PRINCIPAL
// ============================================================================

export const useGetColumns = (filters, expandedId, setExpandedId, rows) => {
  const navigate = useNavigate();

  // ==========================================================================
  // HANDLERS DE NAVEGACIÓN
  // ==========================================================================

  /**
   * Navega a la vista de visitas contrato
   * @param {Object} row - Datos de la fila seleccionada
   * @param {boolean} misVisitas - Si son visitas del usuario actual
   */
  const handleClickView = useCallback(
    (row, misVisitas) => {
      navigate(ROUTES.VISITAS_CONTRATO, {
        state: {
          ...row,
          idFiguraProfesional: row.idFiguraProfesional,
          idTipoProfesional: row.idTipoProfesional,
          misVisitas,
        },
      });
    },
    [navigate]
  );

  /**
   * Navega a la vista de notificaciones
   * @param {Object} row - Datos de la fila seleccionada
   */
  const handleClickNotificaciones = useCallback(
    (row) => {
      navigate(ROUTES.NOTIFICACIONES, {
        state: {
          idEvaluacion: row?.idEvaluacion,
        },
      });
    },
    [navigate]
  );

  /**
   * Alterna el estado de expansión de una fila
   * @param {number} idEvaluacionProfesional - ID de la evaluación a expandir/colapsar
   */
  const toggleRow = useCallback(
    (idEvaluacionProfesional) => {
      setExpandedId(
        expandedId === idEvaluacionProfesional ? null : idEvaluacionProfesional
      );
    },
    [expandedId, setExpandedId]
  );

  // ==========================================================================
  // DEFINICIÓN DE COLUMNAS
  // ==========================================================================

  /**
   * Columna de expansión/colapso
   * Renderiza el botón de expand/collapse o el panel de detalle completo
   */
  const expandColumn = useMemo(
    () => ({
      field: "__expand",
      headerName: "",
      flex: 1,
      minWidth: 30,
      maxWidth: 30,
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      noPadding: true,
      renderCell: (params) => {
        const { row } = params;

        // Renderizar panel de detalle si es una fila expandida
        if (isDetailRow(row)) {
          const parentRow = rows.find((r) => r.idEvaluacionProfesional === row.__parentId);
          if (!parentRow) return null;

          const DetalleComponent =
            filters?.tipoVisita === TIPO_VISITA.PACIENTES
              ? DetalleRowPacientes
              : DetalleRowTareas;

          return <DetalleComponent row={parentRow} />;
        }

        // Renderizar botón de expansión para filas normales
        const isExpanded = expandedId == row.idEvaluacionProfesional;
        return (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              toggleRow(row.idEvaluacionProfesional);
            }}
            aria-label={isExpanded ? "Colapsar fila" : "Expandir fila"}
          >
            {isExpanded ? (
              <ExpandLess fontSize="small" />
            ) : (
              <ExpandMore fontSize="small" />
            )}
          </IconButton>
        );
      },
      // Expandir celda para abarcar todas las columnas visibles en filas de detalle
      colSpan: (_, row) => {
        if (!isDetailRow(row)) return 1;
        return filters?.tipoVisita === TIPO_VISITA.PACIENTES
          ? VISIBLE_COL_COUNT_PACIENTES
          : VISIBLE_COL_COUNT_TAREAS;
      },
    }),
    [expandedId, toggleRow, rows, filters?.tipoVisita]
  );

  /**
   * Columna de acciones
   * Contiene menú con opciones: Mis Visitas, Notificaciones, Otras Visitas
   */
  const actionColumn = useMemo(
    () => ({
      headerName: "",
      field: "action",
      flex: 1,
      minWidth: 30,
      maxWidth: 30,
      sortable: false,
      align: "center",
      noPadding: true,
      renderCell: ({ row }) => (
        <PopoverModal>
          <div className="popover-modal-content" style={{ marginTop: "5px" }}>
            <SimplePopoverOption
              onClick={() => handleClickView(row, true)}
              Icon={EmergencyIcon}
              label="Mis Visitas"
            />
            <SimplePopoverOption
              onClick={() => handleClickNotificaciones(row)}
              Icon={NotificationsIcon}
              label="Notificaciones"
            />
            <SimplePopoverOption
              onClick={() => handleClickView(row, false)}
              Icon={MedicalServicesOutlinedIcon}
              label="Otras Visitas"
            />
          </div>
        </PopoverModal>
      ),
    }),
    [handleClickView, handleClickNotificaciones]
  );

  /** Columna de Paciente (solo en vista Tareas) */
  const pacienteColumn = useMemo(
    () => ({
      flex: 8,
      field: "paciente",
      headerName: "Paciente",
    }),
    []
  );

  /** Columna de Tipo de Profesional (solo en vista Tareas) */
  const tipoProfesionalColumn = useMemo(
    () => ({
      flex: 3.5,
      field: "tipoProfesional",
      headerName: "Tipo",
    }),
    []
  );

    const figuraProfesionalColumn = useMemo(
    () => ({
      flex: 3.5,
      field: "figuraProfesional",
      headerName: "Figura Profesional",
    }),
    []
  );

  /** Columna de Notificaciones sin leer (solo en vista Tareas) */
  const notificacionesColumn = useMemo(
    () => ({
      headerName: "",
      flex: 1,
      minWidth: 30,
      maxWidth: 30,
      noPadding: true,
      noPaddingHeader: true,
      field: "cantidadNotificaciones",
      align: "center",
      renderHeader: () => (
        <Tooltip title="Notificaciones sin leer" placement="top">
          <NotificationsIcon />
        </Tooltip>
      ),
    }),
    []
  );

  /** Columna de Profesional (solo en vista Pacientes) */
  const profesionalColumn = useMemo(
    () => ({
      flex: 8,
      field: "profesional",
      headerName: "Profesional",
    }),
    []
  );

  /** Columna de ID de Evaluación (solo en vista Pacientes) */
  const idEvaluacionColumn = useMemo(
    () => ({
      flex: 3,
      field: "idEvaluacion",
      headerName: "#",
    }),
    []
  );

  // ==========================================================================
  // CONSTRUCCIÓN DINÁMICA DE COLUMNAS
  // ==========================================================================

  /**
   * Ensambla las columnas según el tipo de visita
   * - Vista Pacientes (tipo 2): expand + action + profesional + #
   * - Vista Tareas (tipo 1): expand + action + paciente + tipo + notificaciones
   */
  const columns = useMemo(() => {
    const baseColumns = [expandColumn, actionColumn];

    if (filters?.tipoVisita === TIPO_VISITA.PACIENTES) {
      return [...baseColumns, profesionalColumn, idEvaluacionColumn];
    }

    return [
      ...baseColumns,
      pacienteColumn,
      tipoProfesionalColumn,
      figuraProfesionalColumn,
      notificacionesColumn,
    ];
  }, [
    expandColumn,
    actionColumn,
    pacienteColumn,
    tipoProfesionalColumn,
    figuraProfesionalColumn,
    notificacionesColumn,
    profesionalColumn,
    idEvaluacionColumn,
    filters?.tipoVisita,
  ]);

  // Aplicar configuración por defecto y devolver columnas procesadas
  return generateColumnsDefaultConfiguration(columns);
};